import type { ReactElement } from 'react'
/**
 * ExpandConfirmationDialog component for Infrastructure Map
 *
 * Confirmation dialog for recursive collapse/expand operations (Shift+click).
 * Shows the number of affected nodes and asks for confirmation.
 */

import { useTranslation } from 'react-i18next'

interface ExpandConfirmationDialogProps {
  /** Whether dialog is open */
  isOpen: boolean
  /** Whether expanding (true) or collapsing (false) */
  isExpanding: boolean
  /** Number of nodes affected */
  affectedCount: number
  /** Callback when confirmed */
  onConfirm: () => void
  /** Callback when cancelled */
  onCancel: () => void
}

export function ExpandConfirmationDialog({
  isOpen,
  isExpanding,
  affectedCount,
  onConfirm,
  onCancel,
}: ExpandConfirmationDialogProps): ReactElement | null {
  const { t } = useTranslation('cases')

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      {/* Dialog */}
      <div className="animate-in fade-in zoom-in-95 relative mx-4 max-w-md rounded-xl bg-white p-6 shadow-2xl duration-200 dark:bg-slate-800">
        {/* Icon */}
        <div className="mb-4 flex justify-center">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full ${
              isExpanding
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-fuchsia-100 dark:bg-fuchsia-900/30'
            }`}
          >
            <span className="text-2xl">{isExpanding ? '+' : '\u2212'}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="mb-2 text-center text-lg font-semibold text-slate-900 dark:text-slate-100">
          {isExpanding
            ? t('infrastructure.canvas.expandAllLevels')
            : t('infrastructure.canvas.collapseAllLevels')}
        </h3>

        {/* Description */}
        <p className="mb-6 text-center text-sm text-slate-600 dark:text-slate-400">
          {isExpanding
            ? t('infrastructure.canvas.expandRecursiveDesc', { count: affectedCount })
            : t('infrastructure.canvas.collapseRecursiveDesc', { count: affectedCount })}
        </p>

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
              isExpanding
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-fuchsia-600 hover:bg-fuchsia-700'
            }`}
          >
            {isExpanding
              ? t('infrastructure.canvas.expandAll')
              : t('infrastructure.canvas.collapseAll')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExpandConfirmationDialog
